import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toasterMsg } from '../../../../../Components/Toaster/Toaster';
import {
  assignRangePlanRetailBarcodes,
  generateRangePlanGtin14,
} from '../../../../../services/range-plan-identifier-barcodes.service';
import { I18N_NAMESPACE } from './product-identifier-v2.constants';
import type { PendingBarcodeAction } from './types';

const HTTP_FORBIDDEN = 403;
const HTTP_BAD_REQUEST = 400;
const HTTP_NOT_FOUND = 404;

/**
 * The message a refused column action shows. The server's own text is never shown: a 403 means a
 * ticked product is outside the user's access, a 400 or 404 means the request was malformed.
 * @param error - What the API client rejected with.
 * @returns The translation key.
 */
export function barcodeActionErrorKey(error: unknown): string {
  const status = (error as { response?: { status?: number } } | undefined)?.response?.status;
  if (status === HTTP_FORBIDDEN) return 'RANGE_PLAN_PRODUCT_IDENTIFIER.ACTION_DENIED';
  if (status === HTTP_BAD_REQUEST || status === HTTP_NOT_FOUND) return 'RANGE_PLAN_PRODUCT_IDENTIFIER.ACTION_INVALID';
  return 'RANGE_PLAN_PRODUCT_IDENTIFIER.ACTION_FAILED';
}

export function useIdentifierBarcodeActions(selectedIds: string[]) {
  const { t } = useTranslation(I18N_NAMESPACE);
  const [pending, setPending] = useState<PendingBarcodeAction | null>(null);
  const [running, setRunning] = useState(false);

  const request = useCallback((action: PendingBarcodeAction) => setPending(action), []);
  const cancel = useCallback(() => setPending(null), []);

  const confirm = useCallback(async () => {
    if (!pending || !selectedIds.length) return;
    const action = pending;
    setPending(null);
    setRunning(true);
    try {
      if (action.kind === 'retail') {
        await assignRangePlanRetailBarcodes(selectedIds, String(action.barcodeType));
      } else {
        await generateRangePlanGtin14(selectedIds, String(action.factorIndicatorId));
      }
      toasterMsg(t('RANGE_PLAN_PRODUCT_IDENTIFIER.ACTION_STARTED', { label: action.label, count: selectedIds.length }), 'success');
      setRunning(false);
    } catch (error) {
      toasterMsg(t(barcodeActionErrorKey(error)), 'danger');
      setRunning(false);
    }
  }, [pending, selectedIds, t]);

  return { pending, running, request, cancel, confirm };
}
